import axios from "axios";

// Leave REACT_APP_BACKEND_URL empty when Nginx serves the site and /api on the same domain (recommended).
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
export const API_BASE = `${BACKEND_URL}/api`;
export const FILE_BASE = BACKEND_URL;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// ---------- auth tokens ----------
// Cookies are the primary session. The tokens are also kept here and sent as a Bearer header so login
// keeps working when the browser blocks cookies (different domain, http, Safari privacy settings…).
const TOKEN_KEY = "anc_access_token";
const REFRESH_KEY = "anc_refresh_token";
const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { v ? localStorage.setItem(k, v) : localStorage.removeItem(k); } catch { /* ignore */ } },
};
export const setAuthTokens = (access, refresh) => {
  if (access !== undefined) store.set(TOKEN_KEY, access);
  if (refresh !== undefined) store.set(REFRESH_KEY, refresh);
};
export const clearAuthTokens = () => { store.set(TOKEN_KEY, null); store.set(REFRESH_KEY, null); };

api.interceptors.request.use((config) => {
  const t = store.get(TOKEN_KEY);
  if (t && !config.headers?.Authorization) config.headers = { ...config.headers, Authorization: `Bearer ${t}` };
  return config;
});

let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const status = error.response?.status;
    const url = cfg.url || "";
    const isAuthCall = /\/auth\/(login|register|refresh|logout)/.test(url);
    if (status === 401 && !cfg._retried && !isAuthCall) {
      cfg._retried = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh", { refresh_token: store.get(REFRESH_KEY) || undefined })
          .finally(() => { setTimeout(() => { refreshing = null; }, 0); });
        const r = await refreshing;
        if (r.data?.access_token) store.set(TOKEN_KEY, r.data.access_token);
        cfg.headers = { ...cfg.headers, Authorization: `Bearer ${r.data?.access_token || ""}` };
        return api(cfg);
      } catch {
        clearAuthTokens();
      }
    }
    return Promise.reject(error);
  }
);

export const fileUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http") || path.startsWith("data:") || path.startsWith("blob:")) return path;
  if (path.startsWith("/api/")) return `${FILE_BASE}${path}`;
  // legacy "/uploads/..." links are served by the backend from local server storage
  if (path.startsWith("/uploads/")) return `${API_BASE}/files/${path.slice("/uploads/".length)}`;
  // bare storage path like "products/2026/10/uuid.jpg"
  return `${API_BASE}/files/${path.replace(/^\/+/, "")}`;
};

// ---------- YouTube helpers ----------
export const youtubeId = (value) => {
  const v = (value || "").trim();
  if (!v) return null;
  if (/^[A-Za-z0-9_-]{11}$/.test(v)) return v;
  const m = v.match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

/** Embed URL that autoplays muted and loops forever (browsers only allow autoplay when muted). */
export const youtubeEmbedUrl = (id, { controls = false } = {}) => {
  if (!id) return "";
  const params = new URLSearchParams({
    autoplay: "1", mute: "1", loop: "1", playlist: id, controls: controls ? "1" : "0",
    modestbranding: "1", rel: "0", playsinline: "1", iv_load_policy: "3", enablejsapi: "1",
    disablekb: controls ? "0" : "1",
  });
  if (typeof window !== "undefined") params.set("origin", window.location.origin);
  return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
};

export const youtubeThumb = (id) => (id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : "");

export const inr = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const FIELD_LABELS = { name: "Name", price: "Price", category_id: "Category", slug: "Slug", images: "Images", description: "Description" };
export const formatDetail = (detail) => {
  if (!detail) return "";
  if (Array.isArray(detail)) {
    return detail.map((e) => {
      const field = Array.isArray(e?.loc) ? e.loc[e.loc.length - 1] : "";
      const label = FIELD_LABELS[field] || field;
      return label ? `${label}: ${e?.msg || "invalid"}` : (e?.msg || JSON.stringify(e));
    }).join(" · ");
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

/** Sale badge: the admin's "On Sale" tick wins; older products fall back to compare price > price. */
export const isOnSale = (p) => {
  if (!p) return false;
  if (typeof p.on_sale === "boolean") return p.on_sale;
  return !!(p.compare_at_price && Number(p.compare_at_price) > Number(p.price));
};

/** Human message for any axios error, including "can't reach the server". */
export const errorMessage = (e, fallback = "Something went wrong. Please try again.") => {
  if (!e?.response) return e?.message && !/network/i.test(e.message) ? e.message : "Can't reach the server. Check your internet connection and try again.";
  const st = e.response.status;
  if (st === 413) return "This file is larger than the server allows. Ask the developer to raise Nginx 'client_max_body_size' (e.g. 12M).";
  if (st === 502 || st === 503 || st === 504) return "The server is not responding right now (backend stopped or restarting). Please try again in a minute.";
  const d = formatDetail(e.response.data?.detail);
  if (d) return d;
  if (st === 404) return "This feature isn't available on the server yet — the backend needs to be updated and restarted.";
  if (st === 403) return "You don't have permission to do this.";
  return fallback;
};

// ---------- image upload (shrinks big phone photos before sending) ----------
const MAX_DIM = 2000;          // px, longest side
const TARGET_BYTES = 900 * 1024; // stay under Nginx's default 1 MB upload limit

async function loadBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file, { imageOrientation: "from-image" }); } catch { /* fall back */ }
  }
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("This image could not be read. Please use a JPG, PNG or WEBP photo.")); };
    img.src = url;
  });
}

export async function compressImage(file) {
  if (!file || !/^image\/(jpe?g|png|webp)$/i.test(file.type)) return file;           // GIF/PDF etc. untouched
  if (file.size <= TARGET_BYTES && file.type !== "image/png") return file;
  const bmp = await loadBitmap(file);
  const w0 = bmp.width, h0 = bmp.height;
  let dim = Math.min(MAX_DIM, Math.max(w0, h0));
  let blob = null;
  // Try good quality first, then lower quality, then smaller dimensions — until it fits under the limit.
  for (let round = 0; round < 6 && (!blob || blob.size > TARGET_BYTES); round++) {
    const scale = Math.min(1, dim / Math.max(w0, h0));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(w0 * scale)); canvas.height = Math.max(1, Math.round(h0 * scale));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height); // flatten transparency
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    for (const q of [0.86, 0.75, 0.62]) {
      blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", q));
      if (blob && blob.size <= TARGET_BYTES) break;
    }
    dim = Math.round(dim * 0.75);
  }
  if (bmp.close) bmp.close();
  if (!blob) return file;
  if (blob.size >= file.size && file.size <= TARGET_BYTES) return file;
  const name = (file.name || "image").replace(/\.[^.]+$/, "") + ".jpg";
  return new File([blob], name, { type: "image/jpeg" });
}

/** Upload one image (auto-shrunk) and return its URL. Throws an Error with a friendly message. */
export async function uploadImage(file, folder = "products") {
  if (file && /heic|heif/i.test(file.type || file.name)) {
    throw new Error("iPhone HEIC photos aren't supported. Please choose 'Most Compatible' in camera settings or upload a JPG.");
  }
  let toSend = file;
  try { toSend = await compressImage(file); } catch (e) { throw new Error(e?.message || "This image could not be read."); }
  const fd = new FormData();
  fd.append("file", toSend);
  fd.append("folder", folder);
  try {
    const r = await api.post("/upload", fd);
    return r.data.url;
  } catch (e) {
    throw new Error(errorMessage(e, "Image upload failed"));
  }
}

/** Open a protected PDF (invoice, barcode label) in a new tab using the logged-in session. */
export const openPdf = async (path) => {
  const win = window.open("", "_blank"); // open synchronously so pop-up blockers allow it
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    if (win) win.location.href = url; else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (win) win.close();
    throw e;
  }
};
